'use client';

import { useMemo } from 'react';
import { useHydrateAtoms } from 'jotai/utils';
import type { ExtractAtomValue } from 'jotai/vanilla';
import { abTestConfigAtom } from '@/atoms/abTestConfigAtom';
import { pdpParamsAtom } from '@/atoms/pdpParamsAtom';
import {
  blockListClientUrlAtom,
  expressCheckoutConfigAtom,
  imageBaseUrlAtom,
  platformAtom,
  publicApiUrlAtom,
} from '@/atoms/processEnvAtom';
import { cachedPromoConfigAtom, initialPromoCountAtom } from '@/atoms/promoAtom';
import { trendingCategorySlugsAtom } from '@/atoms/trendingSearchesAtom';

// need this because of hydration errors.
const ATOM_MAP = {
  cachedPromoConfigAtom: cachedPromoConfigAtom,
  initialPromoCountAtom: initialPromoCountAtom,
  imageBaseUrlAtom: imageBaseUrlAtom,
  platformAtom: platformAtom,
  blockListClientUrlAtom: blockListClientUrlAtom,
  publicApiUrlAtom: publicApiUrlAtom,
  pdpParamsAtom: pdpParamsAtom,
  expressCheckoutConfigAtom: expressCheckoutConfigAtom,
  abTestConfigAtom: abTestConfigAtom,
  trendingCategorySlugsAtom: trendingCategorySlugsAtom,
};

type AtomKey = keyof typeof ATOM_MAP;
type AtomValue<T extends AtomKey> = ExtractAtomValue<(typeof ATOM_MAP)[T]>;
type AtomMap = Map<(typeof ATOM_MAP)[AtomKey], AtomValue<AtomKey>>;

type AtomHydratorProps = {
  values: Partial<Record<AtomKey, AtomValue<AtomKey>>>;
};

const AtomHydrator = ({ values }: AtomHydratorProps) => {
  const atomMap = useMemo<AtomMap>(() => {
    const atomMap = new Map();

    for (const [atomKey, atomValue] of Object.entries(values)) {
      const atom = ATOM_MAP[atomKey as AtomKey];
      if (!atom) {
        throw new Error(`AtomHydrator: Unknown atom key "${atomKey}"`);
      }
      atomMap.set(atom, atomValue);
    }

    return atomMap;
  }, [values]);

  useHydrateAtoms(atomMap);

  return null;
};

export default AtomHydrator;
